import isSetStateAction from "@jnctn/hooks/utils/isSetStateAction";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

type Serializer<T> = {
  encode(value: T): string;
  decode(value: string): T;
};

const DEFAULT_SERIALIZER = {
  encode: JSON.stringify,
  decode: JSON.parse,
};

// Custom storage event
class CustomStorageEvent<T> extends CustomEvent<T> {
  static eventName = "local-storage-hook";
  constructor(key: string, detail: T) {
    super(CustomStorageEvent.getEventName(key), { detail });
  }
  static getEventName(key: string): string {
    return `${this.eventName}-${key}`;
  }
}

// Allows usage of local storage as if it was useState
export default function useLocalStorage<T>(
  key: string,
  initialValue: T,
  serializer: Serializer<T> = DEFAULT_SERIALIZER
): [T, Dispatch<SetStateAction<T>>] {
  // Enables hooks of the same key to be kept in sync
  const event = useRef<CustomStorageEvent<T>>(null);

  // Handle the dispatching of events
  // this relies on the fact that the hook is get executed when the value updates
  // and so it will see that event.current has changed
  useEffect(() => {
    if (event.current) {
      window.dispatchEvent(event.current);
      // Clear the event
      event.current = undefined;
    }
    // eslint-disable-next-line
  }, [event.current]);

  // Tracks state with initial value from local storage
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? serializer.decode(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  // Stores the value in localStorage and cretes event to keep storage in sync
  const setValue = useCallback(
    (setStateAction: SetStateAction<T>) =>
      setStoredValue((prevState: T): T => {
        const decodedValue = isSetStateAction(setStateAction)
          ? setStateAction(prevState)
          : setStateAction;

        // Only store if the value has changed
        if (decodedValue !== prevState) {
          try {
            const encodedValue = serializer.encode(decodedValue);
            window.localStorage.setItem(key, encodedValue);
            // Set the event to be dispatched
            event.current = new CustomStorageEvent(key, decodedValue);
          } catch (error) {}
        }

        return decodedValue;
      }),
    [key, serializer]
  );

  // Makes sure multiple hooks of the same key are kept in sync
  useEffect(() => {
    function customStorageListener(e: CustomStorageEvent<T>) {
      // If there is an event set, then it is our event so don't update
      if (event.current === undefined) {
        setStoredValue(e.detail);
      }
    }
    function storageListener(e: StorageEvent) {
      if (e.key === key && e.newValue) {
        setStoredValue(serializer.decode(e.newValue));
      }
    }
    const eventName = CustomStorageEvent.getEventName(key);
    // @ts-ignore
    window.addEventListener(eventName, customStorageListener);
    window.addEventListener("storage", storageListener);
    return () => {
      // @ts-ignore
      window.removeEventListener(eventName, customStorageListener);
      window.removeEventListener("storage", storageListener);
    };
  }, [serializer, key]);

  return [storedValue, setValue];
}
